import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Search, Pencil, Trash2, Eye } from 'lucide-react';
import ClientDetail from './ClientDetail';
import ClientFormModal from './ClientFormModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const emptyForm = { type: 'particulier', first_name: '', last_name: '', company_name: '', phone: '', email: '', address: '', postal_code: '', city: '', notes: '' };

export default function ClientsModule({ authHeaders, onNavigate }) {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const fetchClients = useCallback(async () => {
    const res = await axios.get(`${API_URL}/api/crm/clients`, { headers: authHeaders, params: search ? { search } : {} });
    setClients(res.data);
  }, [search, authHeaders]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const openEdit = (c) => { setForm(c); setEditing(c); setShowForm(true); };
  const openNew = () => { setForm({ ...emptyForm }); setEditing(null); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) await axios.put(`${API_URL}/api/crm/clients/${editing.id}`, form, { headers: authHeaders });
      else await axios.post(`${API_URL}/api/crm/clients`, form, { headers: authHeaders });
      setShowForm(false); setEditing(null); fetchClients();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    await axios.delete(`${API_URL}/api/crm/clients/${id}`, { headers: authHeaders });
    fetchClients();
  };

  const viewClient = async (id) => {
    const res = await axios.get(`${API_URL}/api/crm/clients/${id}`, { headers: authHeaders });
    setViewing(res.data);
  };

  if (viewing) return <ClientDetail viewing={viewing} onBack={() => setViewing(null)} />;

  return (
    <div data-testid="clients-module">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Rechercher un client..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#DC2626] focus:outline-none" data-testid="client-search" />
        </div>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700" data-testid="add-client-btn"><Plus size={16} /> Nouveau client</button>
      </div>

      <div className="bg-white rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr><th className="text-left p-3">Client</th><th className="text-left p-3 hidden md:table-cell">Téléphone</th><th className="text-left p-3 hidden md:table-cell">Email</th><th className="text-left p-3 hidden lg:table-cell">Ville</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id} className="border-t hover:bg-gray-50">
                  <td className="p-3"><span className="font-medium">{c.first_name} {c.last_name}</span> {c.company_name && <span className="text-gray-400">({c.company_name})</span>}</td>
                  <td className="p-3 hidden md:table-cell text-gray-600">{c.phone}</td>
                  <td className="p-3 hidden md:table-cell text-gray-600">{c.email}</td>
                  <td className="p-3 hidden lg:table-cell text-gray-600">{c.city}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => viewClient(c.id)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Voir"><Eye size={16} /></button>
                      <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600" title="Modifier"><Pencil size={16} /></button>
                      <button onClick={() => handleDelete(c.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Supprimer"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!clients.length && <tr><td colSpan={5} className="p-8 text-center text-gray-400">Aucun client trouvé</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <ClientFormModal
          form={form} setForm={setForm}
          editing={editing}
          onSave={handleSave}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}
